import React from "react";
import { DraggableProvided } from "react-beautiful-dnd";

import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearchOutlined";
import { Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import { DealCharacteristicsModal } from "components/Characteristics/Deal-characteristics-modal/DealCharacteristicsModal";
import { useNavigate, usePrompt } from "hooks";
import { DealStageItem } from "store/types";

import { getAssetClassName, getCardChips } from "../Pipeline/pipeline-utils";
import { StageDiffTimeline } from "./StageDiffTimeline";

interface Props extends DealStageItem {
  /** Absent while grouping is active — the card renders static (no drag). */
  draggableProvided?: DraggableProvided;
  isDragging?: boolean;
  stageId: number;
  /** Characteristic names to render as chips; falls back to the asset-class subtitle when empty. */
  cardCharacteristicKeys?: string[];
  /** Called after the Deal characteristics modal saves — the board refetches so chips reflect changes. */
  onSaved?: () => void;
}

/**
 * Deal card used while the grouping feature is active — matches the Figma design (node 2549:29170):
 * title + characteristic chips on a `#f6f8fc` surface, no note/avatars footer.
 */
export const PipelineDealCard: React.FC<Props> = (props) => {
  const { id, title, draggableProvided, cardCharacteristicKeys = [], onSaved } = props;
  const asset_class = props.asset_class;
  const openPrompt = usePrompt();
  const navigate = useNavigate();

  const chips = cardCharacteristicKeys.length ? getCardChips(props, cardCharacteristicKeys) : [];

  // The characteristics icon opens the Deal characteristics modal (it does NOT navigate into the deal).
  // The card body itself is not clickable.
  const openCharacteristics = async () => {
    const saved = await openPrompt({
      Component: DealCharacteristicsModal,
      componentType: "modal",
      props: { dealId: id, dealName: title }
    });
    if (saved) onSaved?.();
  };

  const dragProps = draggableProvided
    ? {
        ref: (node: HTMLElement | null) => {
          if (typeof draggableProvided.innerRef === "function") {
            draggableProvided.innerRef(node);
          } else if (draggableProvided.innerRef) {
            (draggableProvided.innerRef as React.MutableRefObject<HTMLElement | null>).current = node;
          }
        },
        ...draggableProvided.draggableProps,
        ...draggableProvided.dragHandleProps,
        style: {
          ...draggableProvided.draggableProps.style,
          userSelect: "none" as const,
          touchAction: "none" as const
        }
      }
    : {};

  return (
    <Stack
      {...dragProps}
      gap="4px"
      sx={{
        bgcolor: "#f6f8fc",
        borderRadius: "8px",
        p: "8px",
        cursor: draggableProvided ? "grab" : "default",
        transition: "background-color 0.15s linear, box-shadow 0.15s linear",
        "&:hover": { bgcolor: "#eef2f9", boxShadow: "0px 1px 4px rgba(10, 49, 66, 0.12)" },
        "&:active": draggableProvided ? { cursor: "grabbing" } : undefined
      }}
    >
      <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#071f35", wordBreak: "break-word" }}>
        {title}
      </Typography>

      {cardCharacteristicKeys.length
        ? // Characteristics are configured — show their chips (or nothing). No asset-class fallback here,
          // otherwise a deal missing the chosen field would wrongly show its asset class.
          chips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap="4px">
              {chips.map((chip) => (
                <Chip
                  key={chip.key}
                  size="small"
                  label={chip.value}
                  sx={{
                    height: "24px",
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "#071f35",
                    bgcolor: "#dbe8f5",
                    borderRadius: "20px",
                    "& .MuiChip-label": { px: "8px" }
                  }}
                />
              ))}
            </Stack>
          )
        : // Nothing configured — fall back to the asset-class subtitle.
          getAssetClassName(asset_class) && (
            <Typography sx={{ fontSize: "13px", lineHeight: "16px", color: "#92a7bc", wordBreak: "break-word" }}>
              {getAssetClassName(asset_class)}
            </Typography>
          )}

      {/* Diff view only (present when the backend returned a stage_diff timeline). */}
      <StageDiffTimeline stageDiff={props.stage_diff} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mt: "8px" }}>
        {/* Opens the Deal characteristics modal (content_paste_search glyph per the design). */}
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            openCharacteristics();
          }}
          sx={{
            p: 0,
            width: "20px",
            height: "20px",
            borderRadius: "32px",
            bgcolor: "#f6f8fc",
            "&:hover": { bgcolor: "#dbe8f5" }
          }}
        >
          <ContentPasteSearchOutlinedIcon sx={{ width: "16px", height: "19px", color: "#071f35" }} />
        </IconButton>

        {/* Open Deal — navigates into the deal (the card body opens the characteristics modal instead). */}
        <Button
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            navigate({ route: "deal", params: { deal_id: String(id) } });
          }}
          sx={{
            minWidth: 0,
            maxWidth: "240px",
            height: "24px",
            minHeight: "24px",
            px: "8px",
            py: 0,
            gap: "4px",
            borderRadius: "32px",
            border: "1px solid #071f35",
            bgcolor: "white",
            color: "#071f35",
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: 400,
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "#eef2f9", border: "1px solid #071f35" }
          }}
        >
          Open Deal
        </Button>
      </Stack>
    </Stack>
  );
};
